import type { SupabaseClient } from '@supabase/supabase-js';
import { AppError, errorInfoSync } from '@antreja/ui/errors';

const authError = (e: unknown, op: string) => new AppError(errorInfoSync(e, `auth.${op}`));

export type Aal = 'aal1' | 'aal2';

export interface AuthUser {
  id: string;
  email: string | null;
}

export interface TotpEnrollment {
  factorId: string;
  /** SVG markup (Supabase returns a data-URI or raw SVG) */
  qrCode: string;
  secret: string;
}

/** Abstraction over Supabase auth so the demo mode can run without a backend. */
export interface AuthService {
  getUser(): Promise<AuthUser | null>;
  onChange(cb: () => void): () => void;
  signInWithPassword(email: string, password: string): Promise<void>;
  sendMagicLink(email: string): Promise<void>;
  /** Inloggen met de 6-cijferige code uit de e-mail (werkt ook als een mailscanner de link al heeft geopend). */
  verifyEmailCode(email: string, code: string): Promise<void>;
  signOut(): Promise<void>;
  getAal(): Promise<{ current: Aal; next: Aal }>;
  /** Verified TOTP factor id, if any. */
  verifiedTotpFactor(): Promise<string | null>;
  enrollTotp(): Promise<TotpEnrollment>;
  verifyTotp(factorId: string, code: string): Promise<void>;
}

export class SupabaseAuthService implements AuthService {
  constructor(private sb: SupabaseClient) {}

  async getUser() {
    const { data } = await this.sb.auth.getSession();
    const u = data.session?.user;
    return u ? { id: u.id, email: u.email ?? null } : null;
  }
  onChange(cb: () => void) {
    const { data } = this.sb.auth.onAuthStateChange(() => cb());
    return () => data.subscription.unsubscribe();
  }
  async signInWithPassword(email: string, password: string) {
    const { error } = await this.sb.auth.signInWithPassword({ email, password });
    if (error) throw authError(error, 'signInWithPassword');
  }
  async sendMagicLink(email: string) {
    const { error } = await this.sb.auth.signInWithOtp({
      email,
      options: { shouldCreateUser: false, emailRedirectTo: window.location.origin },
    });
    if (error) throw authError(error, 'signInWithOtp');
  }
  async verifyEmailCode(email: string, code: string) {
    const { error } = await this.sb.auth.verifyOtp({ email, token: code, type: 'email' });
    if (error) throw authError(error, 'verifyOtp');
  }
  async signOut() {
    await this.sb.auth.signOut();
  }
  async getAal() {
    const { data, error } = await this.sb.auth.mfa.getAuthenticatorAssuranceLevel();
    if (error) throw authError(error, 'mfa.aal');
    return { current: (data.currentLevel ?? 'aal1') as Aal, next: (data.nextLevel ?? 'aal1') as Aal };
  }
  async verifiedTotpFactor() {
    const { data, error } = await this.sb.auth.mfa.listFactors();
    if (error) throw authError(error, 'mfa.listFactors');
    return data.totp.find((f) => f.status === 'verified')?.id ?? null;
  }
  async enrollTotp() {
    // Clean up half-finished enrolments so a new QR can be generated.
    const { data: list } = await this.sb.auth.mfa.listFactors();
    for (const f of list?.all ?? []) {
      if (f.factor_type === 'totp' && f.status === 'unverified') await this.sb.auth.mfa.unenroll({ factorId: f.id });
    }
    const { data, error } = await this.sb.auth.mfa.enroll({ factorType: 'totp', friendlyName: 'Antreja portaal' });
    if (error) throw authError(error, 'mfa.enroll');
    if (data.type !== 'totp') throw new Error('Onverwacht type tweede factor');
    return { factorId: data.id, qrCode: data.totp.qr_code, secret: data.totp.secret };
  }
  async verifyTotp(factorId: string, code: string) {
    const { error } = await this.sb.auth.mfa.challengeAndVerify({ factorId, code });
    if (error) throw authError({ ...error, message: error.message, code: (error as { code?: string }).code ?? 'mfa_verification_failed' }, 'mfa.verify');
  }
}

/** Demo: personas instead of accounts; TOTP accepts any 6 digits. Nothing leaves the browser. */
export class DemoAuthService implements AuthService {
  private user: AuthUser | null = null;
  private aal: Aal = 'aal1';
  private enrolled = false;
  private listeners = new Set<() => void>();

  signInAsPersona(profileId: string) {
    this.user = { id: profileId, email: null };
    this.aal = 'aal1';
    this.emit();
  }
  private emit() {
    this.listeners.forEach((l) => l());
  }
  async getUser() {
    return this.user;
  }
  onChange(cb: () => void) {
    this.listeners.add(cb);
    return () => {
      this.listeners.delete(cb);
    };
  }
  async signInWithPassword() {
    throw new Error('In de demo log je in door hieronder een rol te kiezen.');
  }
  async sendMagicLink() {
    throw new Error('In de demo worden geen e-mails verstuurd. Kies hieronder een rol.');
  }
  async verifyEmailCode() {
    throw new Error('In de demo worden geen e-mails verstuurd. Kies hieronder een rol.');
  }
  async signOut() {
    this.user = null;
    this.aal = 'aal1';
    this.emit();
  }
  async getAal() {
    return { current: this.aal, next: this.enrolled ? ('aal2' as Aal) : this.aal };
  }
  async verifiedTotpFactor() {
    return this.enrolled ? 'demo-factor' : null;
  }
  async enrollTotp() {
    return {
      factorId: 'demo-factor',
      qrCode: '',
      secret: 'DEMO DEMO DEMO DEMO',
    };
  }
  async verifyTotp(_factorId: string, code: string) {
    if (!/^\d{6}$/.test(code)) throw new Error('Vul de 6 cijfers uit je authenticator-app in.');
    // Demo: 000000 gedraagt zich als een foute code (zo is de foutmelding te zien en te testen).
    if (code === '000000') throw Object.assign(new Error('Invalid TOTP code entered'), { name: 'AuthApiError', code: 'mfa_verification_failed', status: 422 });
    this.enrolled = true;
    this.aal = 'aal2';
    this.emit();
  }
}
